// src/components/RailProducts.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { addToCart } from "../lib/cartStore";
import { productUrl } from "../lib/products";
import { formatSentenceCase } from "../utils/textFormat";

export default function RailProducts({ title, subtitle, products = [] }) {
  const rowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [qtyById, setQtyById] = useState({});

  const visibleProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products]
  );

  // --- gestion flèches ---
  const updateArrows = () => {
    const el = rowRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [visibleProducts.length]);

  const scrollByAmount = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const delta = dir === "right" ? el.clientWidth * 0.9 : -el.clientWidth * 0.9;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // --- bouton réactif + / - ---
  const changeQty = (p, delta) => {
    if (!delta) return;

    setQtyById((prev) => {
      const current = prev[p.id] || 0;
      const next = Math.max(current + delta, 0);
      const copy = { ...prev };
      if (next === 0) delete copy[p.id];
      else copy[p.id] = next;
      return copy;
    });

    addToCart({
      pid: p.id,
      vendorId: p.vendorId || p.VendorId || "goshop",
      qty: delta,
    });
  };

  if (!visibleProducts.length) return null;

  return (
    <div className="card-modern p-3 rail-section">
      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <div>
          <h4 className="mb-0">{title}</h4>
          {subtitle && <div className="text-muted small">{subtitle}</div>}
        </div>
        <Link to="/catalog" className="link-soft small d-flex align-items-center gap-1">
          <span>Voir tout</span>
          <span aria-hidden="true">›</span>
        </Link>
      </div>

      <div className="rail-wrapper position-relative">
        {/* Flèche gauche */}
        {canScrollLeft && (
          <button
            type="button"
            className="rail-nav-btn rail-nav-left"
            onClick={() => scrollByAmount("left")}
            aria-label="Voir les produits précédents"
          >
            ‹
          </button>
        )}

        {/* Rail */}
        <div className="rail-w-row" ref={rowRef}>
          {visibleProducts.map((p) => {
            const price = Number(p.price || 0);
            const oldPrice = p.oldPrice || p.Cass_Stock_Price || null;
            const qty = qtyById[p.id] || 0;

            return (
              <div className="rail-w-item" key={p.id}>
                <Link to={productUrl(p)} className="rail-w-card text-decoration-none d-flex flex-column">
                  {/* Image + cœur */}
                  <div className="rail-w-img">
                    <img
                      src={p.imageUrl || "https://via.placeholder.com/300x300?text=Produit"}
                      alt={p.title || "Produit"}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = "https://via.placeholder.com/300x300?text=Produit";
                      }}
                    />
                    <button
                      type="button"
                      className="rail-w-heart"
                      aria-label="Ajouter aux favoris"
                      onClick={(e) => e.preventDefault()}
                    >
                      ♡
                    </button>
                  </div>

                  {/* Texte */}
                  <div className="rail-w-body flex-grow-1">
                    <div className="rail-w-price-row">
                      <span className="rail-w-price">{price.toLocaleString()} XOF</span>
                      {oldPrice && oldPrice > price && (
                        <span className="rail-w-old">{Number(oldPrice).toLocaleString()} XOF</span>
                      )}
                    </div>

                    <div className="rail-w-title line-clamp-2">{formatSentenceCase(p.title)}</div>

                    {/* ✅ Bouton aligné à gauche */}
                    <div className="mt-2 qty-wrap">
                      {qty === 0 ? (
                        <button
                          type="button"
                          className="rail-w-add-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            changeQty(p, +1);
                          }}
                        >
                          <span className="plus">+</span>
                          Ajouter
                        </button>
                      ) : (
                        <div className="qty-pill">
                          <button
                            type="button"
                            className="qty-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              changeQty(p, -1);
                            }}
                            aria-label="Diminuer"
                          >
                            −
                          </button>

                          <span className="qty-value">{qty}</span>

                          <button
                            type="button"
                            className="qty-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              changeQty(p, +1);
                            }}
                            aria-label="Augmenter"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Flèche droite */}
        {canScrollRight && (
          <button
            type="button"
            className="rail-nav-btn rail-nav-right"
            onClick={() => scrollByAmount("right")}
            aria-label="Voir plus de produits"
          >
            ›
          </button>
        )}
      </div>

      {/* ✅ Styles intégrés (ou copie dans ton CSS global) */}
      <style>{`
        /* Force alignement à gauche */
        .qty-wrap{
          display:flex;
          justify-content:flex-start; /* <-- gauche */
          align-items:center;
        }

        /* Bouton "+ Ajouter" style Walmart */
        .rail-w-add-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:8px;

          height:36px;
          padding:0 14px;

          border:1px solid #d9dde3;
          background:#fff;
          color:#111827;

          border-radius:999px;
          font-weight:800;
          font-size:14px;
          line-height:1;

          box-shadow:0 1px 2px rgba(16,24,40,.08);
          cursor:pointer;
          transition:transform .05s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .rail-w-add-btn:hover{
          border-color:#c7ccd4;
          box-shadow:0 2px 6px rgba(16,24,40,.12);
        }
        .rail-w-add-btn:active{ transform:scale(.98); }
        .rail-w-add-btn:focus-visible{
          outline:3px solid rgba(59,130,246,.35);
          outline-offset:2px;
        }

        /* petit rond + */
        .rail-w-add-btn .plus{
          display:inline-flex;
          width:22px;
          height:22px;
          align-items:center;
          justify-content:center;
          border-radius:999px;
          border:1px solid #d9dde3;
          font-weight:900;
          line-height:1;
        }

        /* Pill quantité (− qty +) */
        .qty-pill{
          display:inline-flex;
          align-items:center;
          justify-content:space-between;

          height:36px;
          min-width:120px;

          padding:0 6px;
          border:1px solid #d9dde3;
          background:#fff;
          border-radius:999px;

          box-shadow:0 1px 2px rgba(16,24,40,.08);
        }

        .qty-btn{
          width:34px;
          height:28px;
          display:inline-flex;
          align-items:center;
          justify-content:center;

          border:none;
          background:transparent;
          border-radius:999px;

          font-size:18px;
          font-weight:900;
          color:#111827;
          cursor:pointer;
          user-select:none;
        }
        .qty-btn:hover{ background:rgba(17,24,39,.06); }
        .qty-btn:active{ transform:scale(.96); }

        .qty-value{
          min-width:28px;
          text-align:center;
          font-weight:900;
          font-size:14px;
          color:#111827;
        }
        /* Le bloc qui contient le bouton doit prendre toute la largeur */
.qty-wrap{
  width: 100%;
  display: flex;
  justify-content: flex-start; /* gauche */
  align-items: center;
}

/* Force le bouton à coller à gauche même si parent est centré */
.rail-w-add-btn{
  margin-left: 0 !important;
  margin-right: auto !important; /* pousse à gauche */
}

/* Pareil pour le pill - qty + */
.qty-pill{
  margin-left: 0 !important;
  margin-right: auto !important;
}

/* (Optionnel) si ton body ou card est centré */
.rail-w-body{
  text-align: left;
}

      `}</style>
    </div>
  );
}

